import React, { useState, useEffect } from 'react';
import useTranslation from '../../hooks/useTranslation';
import { useNavigate, useLocation } from 'react-router-dom';
import { projectService } from '../../services/projectService';
import Icon from '../../components/AppIcon';
import { formatCurrency as fmtCAD } from '../../utils/formatCurrency';

/**
 * ExpenseRedirectPage
 * Shown when a user selects "Expense" from the Document Type modal in Invoice Inbox.
 * Lets the user pick a project, then navigates to that project's ExpensesPage
 * with the inbox document pre-loaded.
 */
const ExpenseRedirectPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const inboxItem = location?.state?.inboxItem || null;

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selecting, setSelecting] = useState(false);

  useEffect(() => {
    projectService?.getAll()
      ?.then(data => setProjects(data || []))
      ?.catch(console.error)
      ?.finally(() => setLoading(false));
  }, []);

  const filteredProjects = projects?.filter(p => {
    if (!search?.trim()) return true;
    const q = search?.toLowerCase();
    return (
      p?.name?.toLowerCase()?.includes(q) ||
      p?.projectNumber?.toLowerCase()?.includes(q)
    );
  });

  const handleSelectProject = (project) => {
    if (selecting) return;
    setSelecting(true);
    navigate(`/project/${project?.id}/expenses`, {
      state: {
        inboxItem,
        fromInbox: true,
        project,
      },
    });
  };

  const handleBack = () => {
    navigate('/invoice-inbox');
  };

  return (
    <div className="min-h-screen bg-[#FAFBFC] flex items-start justify-center pt-16 px-4">
      <div className="w-full max-w-lg">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={handleBack}
            className="p-2 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
          >
            <Icon name="ArrowLeft" size={16} />
          </button>
          <div>
            <h1 className="text-base font-semibold text-gray-900">{t('expenseRedirect.title')}</h1>
            <p className="text-xs text-gray-500 mt-0.5">
              {t('expenseRedirect.subtitle')}
            </p>
          </div>
        </div>

        {/* Inbox doc preview */}
        {inboxItem && (
          <div className="flex items-center gap-3 px-4 py-3 mb-5 bg-blue-50 border border-blue-100 rounded-xl">
            <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
              <Icon name="Receipt" size={16} className="text-blue-500" />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-medium text-gray-900 truncate">{inboxItem?.fileName || 'Document'}</p>
              {inboxItem?.extractedData?.supplier && (
                <p className="text-[10px] text-gray-500 mt-0.5">
                  {inboxItem?.extractedData?.supplier}
                  {inboxItem?.extractedData?.amount ? ` · ${fmtCAD(inboxItem?.extractedData?.amount)}` : ''}
                </p>
              )}
            </div>
            <span className="ml-auto flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-100 text-blue-600">
              <Icon name="Receipt" size={10} />
              Expense
            </span>
          </div>
        )}

        {/* Search */}
        <div className="relative mb-3">
          <Icon name="Search" size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e?.target?.value)}
            placeholder="Search projects..."
            className="w-full h-9 pl-8 pr-3 text-xs rounded-lg border border-gray-200 bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
          />
        </div>

        {/* Project list */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Icon name="Loader2" size={28} className="animate-spin text-primary" />
          </div>
        ) : filteredProjects?.length === 0 ? (
          <div className="text-center py-12 text-gray-400 text-sm">No projects found</div>
        ) : (
          <div className="space-y-2">
            {filteredProjects?.map(project => (
              <button
                key={project?.id}
                onClick={() => handleSelectProject(project)}
                disabled={selecting}
                className="w-full flex items-center gap-3 px-4 py-3 bg-white border border-gray-200 rounded-xl hover:border-primary hover:bg-orange-50/30 transition-all text-left group disabled:opacity-60"
              >
                <div className="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center flex-shrink-0 group-hover:bg-orange-100 transition-colors">
                  <Icon name="FolderOpen" size={15} className="text-primary" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold text-gray-900 truncate">{project?.name || 'Unnamed Project'}</p>
                  {project?.projectNumber && (
                    <p className="text-[10px] text-gray-400 mt-0.5">{project?.projectNumber}</p>
                  )}
                </div>
                <Icon name="ChevronRight" size={14} className="text-gray-300 group-hover:text-primary transition-colors flex-shrink-0" />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ExpenseRedirectPage;
